import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Form,
  FormControl,
  FormField,
  RadioGroup,
  RadioGroupItem,
} from '@tg-web/components';

import { AllowedLang, AllowedLangs } from '../../../shared/lib/useAppLang';
import { ViewField } from '../../../shared/ui/ViewField';

const formSchema = z.object({
  languageCode: z.enum(AllowedLangs),
  theme: z.enum(['dark', 'light', 'system']),
});
type FormType = z.infer<typeof formSchema>;

const langOptions: Array<[AllowedLang, string]> = [
  ['en', 'English'],
  ['pt-BR', 'Português brasileiro'],
  ['fr', 'Français'],
  ['ru', 'Русский'],
  ['de', 'Deutsch'],
  ['zh-CN', '简体中文'],
];

const colorSchemeOptions: Array<['dark' | 'light' | 'system', string]> = [
  ['light', 'all:settings.appearance.color_options.light'],
  ['dark', 'all:settings.appearance.color_options.dark'],
];

export interface AppearanceSettingsFormProps {
  defaultValues: FormType;
  isLoading: boolean;
  onSubmit: (data: FormType) => void;
}

export function AppearanceSettingsForm({
  defaultValues,
  isLoading,
  onSubmit,
}: AppearanceSettingsFormProps) {
  const { t } = useTranslation();

  const form = useForm<FormType>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  return (
    <Form {...form}>
      <form
        className="flex h-full flex-col gap-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-3">
          <p className="typo-text-bold">
            {t('all:settings.appearance.interface_language')}
          </p>
          <FormField
            render={({ field }) => (
              <FormControl>
                <RadioGroup
                  className="flex flex-col rounded-2xl"
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  {langOptions.map(([value, label]) => (
                    <ViewField
                      as="label"
                      className="min-h-[56px] cursor-pointer p-4"
                      htmlFor={value}
                      innerClassName="flex items-center justify-between w-full gap-2"
                      key={value}
                    >
                      <div className="overflow-hidden">
                        <p className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {label}
                        </p>
                      </div>
                      <RadioGroupItem id={value} value={value} />
                    </ViewField>
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            control={form.control}
            name="languageCode"
          />
        </div>
        <div className="flex flex-col gap-3">
          <p className="typo-text-bold">
            {t('all:settings.appearance.color_scheme')}
          </p>
          <FormField
            render={({ field }) => (
              <FormControl>
                <RadioGroup
                  className="flex flex-col rounded-2xl"
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                >
                  {colorSchemeOptions.map(([value, label]) => (
                    <ViewField
                      as="label"
                      className="min-h-[56px] cursor-pointer p-4"
                      htmlFor={value}
                      innerClassName="flex items-center justify-between w-full gap-2"
                      key={value}
                    >
                      <div className="overflow-hidden">
                        <p className="overflow-hidden text-ellipsis whitespace-nowrap">
                          {t(label)}
                        </p>
                      </div>
                      <RadioGroupItem id={value} value={value} />
                    </ViewField>
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            control={form.control}
            name="theme"
          />
        </div>
        <Button isLoading={isLoading} variant="primary">
          {t('all:settings.appearance.button')}
        </Button>
      </form>
    </Form>
  );
}
